import { CurrencyPipe, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { NgxTolgeeModule } from '@tolgee/ngx';

import { GiftCartItem, StandardCartItem } from '../../../../../core/model/cart/cart-item.model';
import { NotificationService } from '../../notification/notification.service';
import { HoverGradientDirective } from '../../shared/directives/hover-gradient.directive';
import { ProductGroup } from '../../shared/model/product-group.model';
import { Product } from '../../shared/model/product.model';
import { CartService } from '../../shared/services/cart.service';
import { SiteService } from '../../shared/services/site.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-product-card',
  imports: [
    NgOptimizedImage,
    HoverGradientDirective,
    CurrencyPipe,
    NgxTolgeeModule,
    RouterLink,
  ],
  templateUrl: './product-card.component.html'
})
export class ProductCardComponent {
  siteService = inject(SiteService);
  cartService = inject(CartService);
  notificationService = inject(NotificationService);
  product = input.required<Product | ProductGroup>();
  gift = input(false);
  giftMode = signal(false);
  route = inject(ActivatedRoute);
  selectedProduct = computed(() => {
    const product = this.product();
    return product instanceof ProductGroup ?
      product.products.reduce((cheapest, product) => {
        const cheapestPrices = this.siteService.price(cheapest.id);
        const tentativePrice = this.siteService.price(product.id);
        return cheapestPrices.price - tentativePrice.price < 0 ?
          cheapest :
          product
      },
      product.products[0]) :
      product;
  });
  isGroup = computed(() => this.product() instanceof ProductGroup);
  price = computed(() => this.siteService.price(this.selectedProduct().id));

  constructor() {
    this.route.queryParamMap
      .pipe(takeUntilDestroyed())
      .subscribe(map => {
        this.giftMode.set(map.get('gift') === 'true');
      });
  }

  /**
   * Add a product to cart
   * @param event Click event
   */
  async addToCart(event: MouseEvent): Promise<void> {
    event.stopPropagation();
    await this.cartService.add([{
      cartItem: this.giftMode() ?
        new GiftCartItem({ product: this.selectedProduct().id }) :
        new StandardCartItem({ product: this.selectedProduct().id, spaBooking: null }),
      quantity: 1
    }]);
    this.notificationService.open({
      type: 'success',
      title: '',
      message: 'addToCartSuccess',
      close: 3000
    });
  }
}
